import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Download, 
  TrendingUp, 
  TrendingDown, 
  Star, 
  Wrench, 
  ClipboardCheck, 
  Clock, 
  Shield, 
  DollarSign,
  User,
  Map,
  Phone,
  Calendar,
  Mail
} from 'lucide-react';
import { getStoresData } from '../../data/mockData';

// Score Card Component
const ScoreCard = ({ title, score, maxScore, icon: Icon, color, change, details }) => {
  const percentage = (score / maxScore) * 100;
  
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-center">
          <div className={`p-2.5 rounded-lg ${color.bg} bg-opacity-20`}>
            <Icon className={`h-5 w-5 ${color.text}`} />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-gray-900">{title}</h3>
            <div className="flex items-baseline">
              <p className="text-2xl font-bold text-gray-900 mr-1">{score}</p>
              <p className="text-sm text-gray-500">/{maxScore}</p>
            </div>
          </div>
        </div>
        {change && (
          <div className={`flex items-center text-xs font-medium ${
            change.trend === 'up' 
              ? 'text-emerald-600' 
              : 'text-rose-600'
          }`}>
            {change.trend === 'up' ? (
              <TrendingUp className="h-3 w-3 mr-0.5" />
            ) : (
              <TrendingDown className="h-3 w-3 mr-0.5" />
            )}
            {change.value}
          </div>
        )}
      </div>

      <div className="mt-4">
        <div className="relative pt-1">
          <div className="overflow-hidden h-2 mb-2 text-xs flex rounded bg-gray-100">
            <div 
              style={{ width: `${percentage}%` }}
              className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${color.bar}`}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div>Score</div>
            <div>{Math.round(percentage)}%</div>
          </div>
        </div>
      </div>

      {details && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-3">
            {details.map((detail, index) => (
              <div key={index} className="flex justify-between items-baseline">
                <span className="text-xs text-gray-500">{detail.label}</span>
                <span className="text-xs font-medium text-gray-900">{detail.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StoreDetail = () => {
  const { storeId } = useParams();
  const [store, setStore] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadStoreData = async () => {
      setIsLoading(true);
      const stores = await getStoresData();
      const foundStore = stores.find(s => s.id === storeId);
      setStore(foundStore);
      setIsLoading(false);
    };

    loadStoreData();
  }, [storeId]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="text-center py-10">
        <h2 className="text-xl font-semibold text-gray-800">Store not found</h2>
        <p className="mt-2 text-gray-600">The store with ID {storeId} could not be found.</p>
        <Link to="/scorecard" className="mt-4 inline-block text-blue-600 hover:underline">
          Return to Scorecard Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-800 rounded-xl p-6 text-white">
        <div className="flex justify-between items-start">
          <div className="flex items-center">
            <Link 
              to="/scorecard"
              className="mr-4 p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-semibold">{store.name}</h1>
              <p className="text-blue-100 mt-1">Store ID: {store.id}</p>
            </div>
          </div>
          <button
            className="px-4 py-2 bg-white text-blue-700 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2 font-medium"
          >
            <Download className="w-5 h-5" />
            Export Report
          </button>
        </div>
      </div>

      {/* Store Info & Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Store Info */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Store Information</h2>
          
          <div className="space-y-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                <Map className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-500">Region</p>
                <p className="text-base font-medium text-gray-900">{store.region}</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                <Map className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-500">City</p>
                <p className="text-base font-medium text-gray-900">{store.city}</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                <User className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-500">Store Manager</p>
                <p className="text-base font-medium text-gray-900">{store.managerName}</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                <Phone className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-500">Contact</p>
                <p className="text-base font-medium text-gray-900">+966 5XX XXX XXX</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-base font-medium text-gray-900">{store.id.toLowerCase()}@company.sa</p>
              </div>
            </div>
            
            <div className="flex items-center">
              <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-500">Last Audit Date</p>
                <p className="text-base font-medium text-gray-900">June 10, 2024</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Total Score */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 lg:col-span-2">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Overall Performance</h2>
              <p className="text-sm text-gray-600 mt-1">Current period scorecard</p>
            </div>
            <div className="bg-white px-4 py-3 rounded-xl shadow-sm border border-gray-200">
              <div className="text-3xl font-bold text-gray-900">{store.totalScore.toFixed(1)}</div>
              <div className="text-sm text-gray-500 text-center">out of 100</div>
            </div>
          </div>
          
          {/* Score Breakdown */}
          <div className="mt-6">
            <div className="relative pt-1">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                    Score Breakdown
                  </span>
                </div>
              </div>
              
              {/* Stacked Progress Bar */}
              <div className="overflow-hidden h-4 mb-4 text-xs flex rounded-full">
                {/* Sales Performance - 30% */}
                <div 
                  style={{ width: `${(store.salesScore / store.totalScore) * 100}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500"
                />
                {/* Google Reviews - 20% */}
                <div 
                  style={{ width: `${(store.reviewsScore / store.totalScore) * 100}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-amber-500"
                />
                {/* Maintenance - 10% */}
                <div 
                  style={{ width: `${(store.maintenanceScore / store.totalScore) * 100}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-rose-500"
                />
                {/* Audit - 10% */}
                <div 
                  style={{ width: `${(store.auditScore / store.totalScore) * 100}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-purple-500"
                />
                {/* Speed - 15% */}
                <div 
                  style={{ width: `${(store.speedScore / store.totalScore) * 100}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-emerald-500"
                />
                {/* Quality - 15% */}
                <div 
                  style={{ width: `${(store.qualityScore / store.totalScore) * 100}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-cyan-500"
                />
              </div>
              
              {/* Legend */}
              <div className="flex flex-wrap gap-3 text-xs font-medium">
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-blue-500 mr-1"></div>
                  <span className="text-gray-700">Sales: {store.salesScore}/30</span>
                </div>
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-amber-500 mr-1"></div>
                  <span className="text-gray-700">Reviews: {store.reviewsScore}/20</span>
                </div>
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-rose-500 mr-1"></div>
                  <span className="text-gray-700">Maintenance: {store.maintenanceScore}/10</span>
                </div>
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-purple-500 mr-1"></div>
                  <span className="text-gray-700">Audit: {store.auditScore}/10</span>
                </div>
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-emerald-500 mr-1"></div>
                  <span className="text-gray-700">Speed: {store.speedScore}/15</span>
                </div>
                <div className="flex items-center">
                  <div className="h-3 w-3 rounded-full bg-cyan-500 mr-1"></div>
                  <span className="text-gray-700">Quality: {store.qualityScore}/15</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Ranking Info */}
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
              <p className="text-sm text-gray-500">Regional Rank</p>
              <p className="text-xl font-bold text-gray-900 mt-1">3rd</p>
              <p className="text-xs text-gray-500 mt-1">of 32 stores</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
              <p className="text-sm text-gray-500">National Rank</p>
              <p className="text-xl font-bold text-gray-900 mt-1">18th</p>
              <p className="text-xs text-gray-500 mt-1">of 104 stores</p>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
              <p className="text-sm text-gray-500">Performance</p>
              <p className="text-xl font-bold text-emerald-600 mt-1">+4.3%</p>
              <p className="text-xs text-gray-500 mt-1">vs last period</p>
            </div>
          </div>
        </div>
      </div>

      {/* Score Category Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Sales Performance */}
        <ScoreCard 
          title="Sales Performance"
          score={store.salesScore}
          maxScore={30}
          icon={DollarSign}
          color={{
            bg: 'bg-blue-500',
            text: 'text-blue-500',
            bar: 'bg-blue-500'
          }}
          change={{
            trend: 'up',
            value: '+1.5'
          }}
          details={[
            { label: 'Sales Growth', value: `${store.salesMetrics.growth}%` },
            { label: 'Target Achievement', value: `${store.salesMetrics.targetAchievement}%` },
            { label: 'Average Transaction', value: `SAR ${store.salesMetrics.avgTransaction}` },
            { label: 'Customer Count', value: store.salesMetrics.customerCount.toLocaleString() }
          ]}
        />

        {/* Google Reviews */}
        <ScoreCard 
          title="Google Reviews"
          score={store.reviewsScore}
          maxScore={20}
          icon={Star}
          color={{
            bg: 'bg-amber-500',
            text: 'text-amber-500',
            bar: 'bg-amber-500'
          }}
          change={{
            trend: 'up',
            value: '+0.8'
          }}
          details={[
            { label: 'Average Rating', value: store.reviewMetrics.avgRating },
            { label: 'Total Reviews', value: store.reviewMetrics.totalReviews },
            { label: 'Positive Sentiment', value: `${store.reviewMetrics.positiveSentiment}%` },
            { label: 'Response Rate', value: `${store.reviewMetrics.responseRate}%` }
          ]}
        />

        {/* Maintenance */}
        <ScoreCard 
          title="Maintenance"
          score={store.maintenanceScore}
          maxScore={10}
          icon={Wrench}
          color={{
            bg: 'bg-rose-500',
            text: 'text-rose-500',
            bar: 'bg-rose-500'
          }}
          change={{
            trend: 'down',
            value: '-0.3'
          }}
          details={[
            { label: 'Active Issues', value: store.maintenanceMetrics.activeIssues },
            { label: 'Avg Resolution', value: `${store.maintenanceMetrics.avgResolutionDays}d` },
            { label: 'Preventive Maint.', value: `${store.maintenanceMetrics.preventiveMaintPct}%` },
            { label: 'Critical Issues', value: store.maintenanceMetrics.criticalIssues }
          ]}
        />

        {/* Audit Score */}
        <ScoreCard 
          title="Audit Score"
          score={store.auditScore}
          maxScore={10}
          icon={ClipboardCheck}
          color={{
            bg: 'bg-purple-500',
            text: 'text-purple-500',
            bar: 'bg-purple-500'
          }}
          change={{
            trend: 'up',
            value: '+0.5'
          }}
          details={[
            { label: 'Last Audit', value: store.auditMetrics.lastAudit },
            { label: 'Cleanliness', value: `${store.auditMetrics.cleanliness}%` },
            { label: 'SOP Compliance', value: `${store.auditMetrics.sopCompliance}%` },
            { label: 'Safety', value: `${store.auditMetrics.safety}%` }
          ]}
        />

        {/* Speed of Service */}
        <ScoreCard 
          title="Speed of Service"
          score={store.speedScore}
          maxScore={15}
          icon={Clock}
          color={{
            bg: 'bg-emerald-500',
            text: 'text-emerald-500',
            bar: 'bg-emerald-500'
          }}
          change={{
            trend: 'up',
            value: '+0.9'
          }}
          details={[
            { label: 'Avg Service Time', value: `${store.serviceMetrics.avgServiceTime}s` },
            { label: 'Drive-thru Time', value: `${store.serviceMetrics.driveThruTime}s` },
            { label: 'Counter Time', value: `${store.serviceMetrics.counterTime}s` },
            { label: 'Order Accuracy', value: `${store.serviceMetrics.orderAccuracy}%` }
          ]}
        />

        {/* Quality Control */}
        <ScoreCard 
          title="Quality Control"
          score={store.qualityScore}
          maxScore={15}
          icon={Shield}
          color={{
            bg: 'bg-cyan-500',
            text: 'text-cyan-500',
            bar: 'bg-cyan-500'
          }}
          change={{
            trend: 'down',
            value: '-0.2'
          }}
          details={[
            { label: 'Product Quality', value: `${store.qualityMetrics.productQuality}%` },
            { label: 'Presentation', value: `${store.qualityMetrics.presentation}%` },
            { label: 'Recipe Adherence', value: `${store.qualityMetrics.recipeAdherence}%` },
            { label: 'Temperature', value: `${store.qualityMetrics.temperature}%` }
          ]}
        />
      </div>
    </div>
  );
};

export default StoreDetail; 